export type AlertType = "SERVER_MESSAGE" | "DATASETS_WARN" | "TEXT";
export type AlertPriority = "NOTICE" | "WARNING" | "CRITICAL";
export type AlertTargetType = "GLOBAL" | "COMPANY" | "USER";
export type TriggerType =
  | "DATASET_VALUE_CHANGED"
  | "DATASET_VALUE_EQUALS"
  | "DATASET_VALUE_IN_RANGE"
  | "PRODUCT_TEMPLATE_VALUE_CHANGED";
export type TriggerLogic = "ANY" | "UNANIMOUS";

export type AlertDTO = {
  id: number;
  alertType: AlertType;
  priority: AlertPriority;
  message: string | null;
  shortMessage: string | null;
  targetType: AlertTargetType;
  createdAt: string;
  timeoutAt: string;
  isAcknowledged: boolean;
};

export type AcknowledgeResponse = {
  success: boolean;
  alertId: number;
  userId: number;
};

export type ClearExpiredResponse = {
  clearedCount: number;
};

export type AlertRuleDTO = {
  id: number;
  name: string;
  enabled: boolean;
  alertType: AlertType;
  priority: AlertPriority;
  message: string | null;
  shortMessage: string | null;
  timeoutMinutes: number;
  targetType: AlertTargetType;
  targetSettings: Record<string, unknown> | null;
  triggerLogic: TriggerLogic;
  triggers: AlertTriggerDTO[];
  createdAt: string;
  updatedAt: string;
  createdById: number;
  createdByName: string;
  updatedById: number | null;
  updatedByName: string | null;
};

export type AlertTriggerDTO = {
  id?: number;
  name: string;
  triggerType: TriggerType;
  settings: TriggerSettings;
  retentionTime: number;
  fireTime: string | null;
  ruleCount?: number; // Number of rules using this trigger
};

export type TriggerSettings =
  | DatasetValueChangedSettings
  | DatasetValueEqualsSettings
  | DatasetValueInRangeSettings
  | ProductTemplateValueChangedSettings;

export type DatasetValueChangedSettings = {
  triggerType: "DATASET_VALUE_CHANGED";
  datasetId: string;
  lastKnownValue?: number;
  lastKnownTime?: string;
  [key: string]: unknown;
};

export type DatasetValueEqualsSettings = {
  triggerType: "DATASET_VALUE_EQUALS";
  datasetId: string;
  value: number;
  tolerance?: number;
  [key: string]: unknown;
};

export type DatasetValueInRangeSettings = {
  triggerType: "DATASET_VALUE_IN_RANGE";
  datasetId: string;
  min: number;
  max: number;
  inclusive?: boolean;
  [key: string]: unknown;
};

export type ProductTemplateValueChangedSettings = {
  triggerType: "PRODUCT_TEMPLATE_VALUE_CHANGED";
  productTemplateId: string;
  fieldName: string;
  [key: string]: unknown;
};

export type TargetSettings =
  | { targetType: "GLOBAL" }
  | { targetType: "COMPANY"; companyRef: string }
  | { targetType: "USER"; userId: number };

/**
 * Request to create or associate a trigger with a rule.
 * If id is provided, the existing trigger will be associated.
 * If id is null, a new trigger will be created with the provided fields.
 */
export type CreateAlertTriggerRequest = {
  id?: number; // If provided, use existing trigger
  name?: string; // Required for new triggers
  settings?: TriggerSettings; // Required for new triggers (must include triggerType)
  retentionTime?: number; // Required for new triggers
};

export type CreateAlertRuleRequest = {
  name: string;
  enabled: boolean;
  alertType: AlertType;
  priority: AlertPriority;
  typeSettings?: Record<string, unknown>;
  targetSettings?: TargetSettings;
  triggerLogic: TriggerLogic;
  triggers: CreateAlertTriggerRequest[];
  companyRef: string;
};

export type UpdateAlertRuleRequest = CreateAlertRuleRequest & {
  id: number;
};

/**
 * Request for creating a standalone reusable trigger
 */
export type CreateStandaloneTriggerRequest = {
  name: string;
  settings: TriggerSettings; // Must include triggerType
  retentionTime: number;
  companyRef: string;
};

export const ALERT_TYPE_OPTIONS: { label: string; value: AlertType }[] = [
  { label: "Server Message", value: "SERVER_MESSAGE" },
  { label: "Datasets Warning", value: "DATASETS_WARN" },
  { label: "Rich Text (HTML)", value: "TEXT" },
];

export const ALERT_PRIORITY_OPTIONS: { label: string; value: AlertPriority }[] = [
  { label: "Notice", value: "NOTICE" },
  { label: "Warning", value: "WARNING" },
  { label: "Critical", value: "CRITICAL" },
];

export const TARGET_TYPE_OPTIONS: { label: string; value: AlertTargetType }[] = [
  { label: "Global (All Users)", value: "GLOBAL" },
  { label: "Company", value: "COMPANY" },
  { label: "Specific User", value: "USER" },
];

export const TRIGGER_TYPE_OPTIONS: { label: string; value: TriggerType }[] = [
  { label: "Dataset Value Changed", value: "DATASET_VALUE_CHANGED" },
  { label: "Dataset Value Equals", value: "DATASET_VALUE_EQUALS" },
  { label: "Dataset Value In Range", value: "DATASET_VALUE_IN_RANGE" },
  { label: "Product Template Value Changed", value: "PRODUCT_TEMPLATE_VALUE_CHANGED" },
];

export const TRIGGER_LOGIC_OPTIONS: { label: string; value: TriggerLogic }[] = [
  { label: "Any (OR)", value: "ANY" },
  { label: "All (AND)", value: "UNANIMOUS" },
];
